import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from "react-redux";
import Sidebar from "../components/Sidebar";
import { API_URL, APPBAR } from "../actions/auth";
import axios from 'axios';

/*
const api = axios.create({
  baseURL: API_URL,
  headers: { Accept: 'application/json' },
});
*/

const IMAGE_CATEGORIES = ['Profile Photo', 'Birth Certificate', 'Aadhaar'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Normalizes whatever the backend returns for a student's images into
// { category, url, file, isNew } objects, keyed by category (missing
// categories simply have no entry, which renders as an empty upload slot).
function normalizeImages(images) {
  const byCategory = {};
  if (Array.isArray(images)) {
    images.forEach((img, idx) => {
      const category = img.category ?? IMAGE_CATEGORIES[idx] ?? 'Other';
      byCategory[category] = {
        id: img.id ?? `existing-${category}-${idx}`,
        category,
        url: img.url ?? img,
        file: null,
        isNew: false,
      };
    });
  }
  return byCategory;
}

// ---------------------------------------------------------------------------
// Single image slot: category label + preview + upload + remove
// ---------------------------------------------------------------------------
function ImageSlot({ category, image, onReplace, onRemove, disabled }) {
  const inputId = `image-input-${category.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative group w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
        {image?.url ? (
          <img
            src={image.url}
            alt={`${category} preview`}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-xs text-slate-400 px-2 text-center">No image</span>
        )}

        {!disabled && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
            <label
              htmlFor={inputId}
              className="cursor-pointer text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-500 px-2 py-1 rounded"
            >
              {image?.url ? 'Replace' : 'Upload'}
            </label>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onReplace(file);
                e.target.value = '';
              }}
            />
            {image?.url && (
              <button
                type="button"
                onClick={onRemove}
                className="text-[11px] font-medium text-white bg-red-600 hover:bg-red-500 px-2 py-1 rounded"
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
      <span className="text-xs font-medium text-slate-600 text-center">{category}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form for a single student
// ---------------------------------------------------------------------------
function StudentForm({ student, onChange, onSave, saving, statusMessage }) {
  const handleField = (field) => (e) => {
    onChange({ ...student, [field]: e.target.value });
  };

  const handleImageReplace = (category, file) => {
    const url = URL.createObjectURL(file);
    onChange({
      ...student,
      images: {
        ...student.images,
        [category]: { id: `new-${category}-${Date.now()}`, category, url, file, isNew: true },
      },
    });
  };

  const handleImageRemove = (category) => {
    const nextImages = { ...student.images };
    delete nextImages[category];
    onChange({ ...student, images: nextImages });
  };

  const uploadedCount = IMAGE_CATEGORIES.filter((c) => student.images[c]?.url).length;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
          <input
            type="text"
            required
            value={student.full_name}
            onChange={handleField('full_name')}
            className="w-full rounded-md border border-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 bg-slate-150"
            placeholder=""
            readOnly
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Aadhar Number <span className="text-red-400">*</span> </label>
          <input
            type="text"
            value={student.aadhar_no}
            onChange={handleField('aadhar_no')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input
            type="email"
            required
            value={student.email}
            onChange={handleField('email')}
            className="w-full rounded-md border border-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 bg-slate-150"
            placeholder=""
            readOnly
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Date of Birth <span className="text-red-400">*</span> </label>
          <input
            type="date"
            required
            value={student.birthdate}
            onChange={handleField('birthdate')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
          <input
            type="text"
            value={student.phone_number}
            onChange={handleField('phone_number')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Gender <span className="text-red-400">*</span> </label>
          <input
            type="text"
            required
            value={student.gender}
            onChange={handleField('gender')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Mother Name <span className="text-red-400">*</span> </label>
          <input
            type="text"
            required
            value={student.mother_name}
            onChange={handleField('mother_name')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Mother Phone</label>
          <input
            type="text"
            value={student.mother_phone}
            onChange={handleField('mother_phone')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Mother Aadhaar </label>
          <input
            type="text"
            required
            value={student.mother_aadhar}
            onChange={handleField('mother_aadhar')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Father Name  <span className="text-red-400">*</span> </label>
          <input
            type="text"
            required
            value={student.father_name}
            onChange={handleField('father_name')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Father phone</label>
          <input
            type="text"
            value={student.father_phone}
            onChange={handleField('father_phone')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Father Aadhaar</label>
          <input
            type="text"
            required
            value={student.father_aadhar}
            onChange={handleField('father_aadhar')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Nationality</label>
          <input
            type="text"
            required
            value={student.nationality}
            onChange={handleField('nationality')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Religion</label>
          <input
            type="text"
            value={student.religion}
            onChange={handleField('religion')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Caste</label>
          <input
            type="text"
            required
            value={student.caste}
            onChange={handleField('caste')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Mother Tongue</label>
          <input
            type="text"
            value={student.mother_tongue}
            onChange={handleField('mother_tongue')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>

        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-slate-700 mb-1">Blood Group</label>
          <input
            type="text"
            value={student.blood_group}
            onChange={handleField('blood_group')}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder=""
          />
        </div>
      </div>


      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Documents ({uploadedCount}/{IMAGE_CATEGORIES.length} uploaded)
        </label>
        <div className="flex flex-wrap gap-4">
          {IMAGE_CATEGORIES.map((category) => (
            <ImageSlot
              key={category}
              category={category}
              image={student.images[category]}
              disabled={saving}
              onReplace={(file) => handleImageReplace(category, file)}
              onRemove={() => handleImageRemove(category)}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          )}
          {saving ? 'Saving...' : 'Save changes'}
        </button>

        {statusMessage && (
          <span
            className={`text-sm ${
              statusMessage.type === 'error' ? 'text-red-600' : 'text-emerald-600'
            }`}
          >
            {statusMessage.text}
          </span>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main component: tab bar + fetch/update orchestration
// ---------------------------------------------------------------------------
export default function StudentFormsTabbed() {
  const dispatch = useDispatch();
  const [students, setStudents] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [savingIndex, setSavingIndex] = useState(null);
  const [statusByIndex, setStatusByIndex] = useState({});
  
  const state = useSelector((state) => state)

  // The list of students — and therefore the number/set of tabs — comes
  // entirely from the API. There is no manual "add student" flow here.
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
        let cfg = {
            headers: {
                Authorization: state.auth.token,
                "Content-Type": "application/json",
            },
        }
        console.log(state.auth.user.user_id)
        const user_id = state.auth.user.user_id
        let prms = {};
        prms['user_id'] = user_id;
        const res = await axios.post(API_URL + '/get_students',prms,cfg)
        console.log(res.data)
        const data = res.data.students
        console.log(data)
        const normalized = data.map((s) => ({
            user_id: s.user_id,
            full_name: s.full_name,
            birthdate: s.birthdate,
            aadhar_no: s.aadhar_no,
            student_pen: s.aadhar_no,
            phone_number: s.phone_number,
            gender: s.gender,
            email: s.email,
            mother_name: s.mother_name,
            mother_phone: s.mother_phone,
            mother_aadhar: s.mother_aadhar,
            father_name: s.father_name,
            father_phone: s.father_phone,
            father_aadhar: s.father_aadhar,
            mother_tongue: s.mother_tongue,
            religion: s.religion,
            caste: s.caste,
            blood_group: s.blood_group,
            nationality: s.nationality,
            user_image: s.user_image,
            avatar: s.user_avatar,
            address: s.address,
            images: s.images,
      }));
      setStudents(normalized);
    } catch (err) {
      console.log(err)
      setLoadError(err.response?.data?.message || err.message || 'Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const updateStudent = (index, updatedStudent) => {
    setStudents((prev) => prev.map((s, i) => (i === index ? updatedStudent : s)));
  };

  // Build multipart form data (fields + per-category images) and PUT to backend
  const handleSave = async (index) => {
    const student = students[index];
    setSavingIndex(index);
    setStatusByIndex((prev) => ({ ...prev, [index]: null }));
    console.log(typeof(student.images),student.images)
    // setSavingIndex(null);

    try {
      const formData = new FormData();
      formData.append('user_id', student.user_id);
      formData.append('full_name', student.full_name);
      formData.append('birthdate', student.birthdate);
      formData.append('aadhar_no', student.aadhar_no);
      formData.append('phone_number', student.phone_number);
      formData.append('email', student.email);
      formData.append('gender', student.gender);
      formData.append('mother_name', student.mother_name);
      formData.append('mother_phone', student.mother_phone);
      formData.append('mother_aadhar', student.mother_aadhar);
      formData.append('father_name', student.father_name);
      formData.append('father_phone', student.father_phone);
      formData.append('father_aadhar', student.father_aadhar);
      formData.append('mother_tongue', student.mother_tongue);
      formData.append('religion', student.religion);
      formData.append('caste', student.caste);
      formData.append('address', student.address);
      formData.append('blood_group', student.blood_group);
      formData.append('nationality', student.nationality);
      formData.append('photos_profile', student.images['Profile Photo'].file);
      formData.append('photos_dob', student.images['Birth Certificate'].file);
      formData.append('photos_aadhar', student.images['Aadhaar'].file);

      // Existing images to keep, tagged with their category
      const keptExisting = Object.values(student.images)
        .filter((img) => !img.isNew)
        .map((img) => ({ category: img.category, url: img.url }));
      formData.append('existingImages', JSON.stringify(keptExisting));

      // New files — sent alongside a parallel array of categories so the
      // backend knows which upload belongs to which category
      Object.values(student.images)
        .filter((img) => img.isNew && img.file)
        .forEach((img) => {
          formData.append('images', img.file);
          formData.append('imageCategories', img.category);
        });

      // const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      // const res = await axios.post(API_URL + `/update_profile`, formData, config);
      let cfg = { headers: { Authorization: state.auth.token, 'Content-Type': 'multipart/form-data' } }
      const res = await axios.post(API_URL + '/update_profile',formData,cfg);

      const saved = res.data.student ?? res.data;
      updateStudent(index, {
        ...student,
        images: normalizeImages(saved.images ?? Object.values(student.images)),
      });

      setStatusByIndex((prev) => ({
        ...prev,
        [index]: { type: 'success', text: 'Saved successfully.' },
      }));
    } catch (err) {
      setStatusByIndex((prev) => ({
        ...prev,
        [index]: {
          type: 'error',
          text: err.response?.data?.message || err.message || 'Save failed.',
        },
      }));
    } finally {
      setSavingIndex(null);
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    // Add your logout logic here (e.g., clearing tokens, redirecting)
    console.log('Logging out...');
    window.location.href = '/logout';
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center text-slate-500">
        Loading students…
      </div>
    );
  }

  if (!students.length) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center text-slate-500">
        {loadError ? loadError : 'No students found.'}
      </div>
    );
  }
  
  console.log(state.auth.isAuthenticated,state.auth.user)

  if (state.auth.isAuthenticated){  
    return (
    <>
    {/* <Sidebar /> */}


    <header className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          
          {/* Left: Logo */}
          <div className="flex-shrink-0 flex items-center">
            <a href="/" className="flex items-center gap-2">
              <div className="h-8 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                <img style={{ maxHeight: "50px", margin: "10px" }} src={require("../img/" + APPBAR)} />
              </div>
            </a>
          </div>

          {/* Center: Title */}
          <div className="flex-1 text-center px-4">
            <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
              Student Profile
            </h1>
          </div>

          {/* Right: Logout Link */}
          <div className="flex-shrink-0 flex items-center">
            <button
              onClick={handleLogout}
              className="text-sm font-medium text-gray-600 hover:text-red-600 transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Logout
            </button>
          </div>

        </div>
      </div>
    </header>



    <div className="max-w-6xl mx-auto p-4 sm:p-6">

      {loadError && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {loadError}
        </div>
      )}

      {/* Tab bar — one tab per student returned by the API */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-6">
        {students.map((s, i) => (
          <button
            key={s.id ?? i}
            type="button"
            onClick={() => setActiveIndex(i)}
            className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
              i === activeIndex
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            {s.full_name?.trim() ? s.full_name : `Student ${i + 1}`}
          </button>
        ))}
      </div>

      {/* Active tab content */}
      <div className="rounded-lg border border-slate-200 p-5 bg-white shadow-sm">
        {students[activeIndex] && (
          <StudentForm
            student={students[activeIndex]}
            onChange={(updated) => updateStudent(activeIndex, updated)}
            onSave={() => handleSave(activeIndex)}
            saving={savingIndex === activeIndex}
            statusMessage={statusByIndex[activeIndex]}
          />
        )}
      </div>
    </div>
    </>
  )
  }else{
      dispatch({ type: "AUTH_ERROR" });
  }
}
